import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { useCallback, useEffect, useMemo } from 'react'
import { EventInput } from '@fullcalendar/core'
import { buildPath, paths } from 'routes'

import { ButtonV2, ErrorState, Icon, Popover, Text, Calendar } from 'components'
import { Loading } from 'domains/serviceOrder/components'
import {
  CalendarBlockingDrawer,
  ConfirmScheduleModal,
  Form,
} from './components'

import { isServiceOrderReschedulable } from 'domains/serviceOrders/utilities'
import { useToast, useToggle, useUserInfo } from 'shared/hooks'
import { handleRenderState } from 'utilities/handleRenderState'
import {
  validateTechnicianScheduleBlocked,
  buildScheduleBlockEvents,
  buildServiceOrderEvent,
  buildGetSOFilters,
  buildSOSchedule,
  filterSchedules,
  isAnotherWeek,
  createScheduledDate,
} from './utils'

import { useGetServiceOrder, useGetServiceOrders } from 'services/serviceOrder'
import { useValidateTechnicianSchedules } from 'services/technicianSchedule'
import { ScheduleType } from 'components/SOCalendarModal/types'
import { scheduleSOSchema } from './schema/scheduleSOSchema'
import { joiResolver } from '@hookform/resolvers/joi'
import { ATTENDANCE_ROLE } from 'services/coverages'
import {
  useCreateIntervention,
  ServiceOrderPayload,
  statusText,
} from 'services/serviceOrderV2'

import styles from './styles.module.scss'

const MAX_EVENTS_PER_DATE = 6

const Schedule = () => {
  const { serviceOrderId } = useParams()
  const { userInfo } = useUserInfo()
  const confirmModal = useToggle()
  const { addToast } = useToast()
  const navigate = useNavigate()

  const { state: serviceOrderState } = useLocation()

  const { isLoading, refetch, isError } = useGetServiceOrder(serviceOrderId)

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: joiResolver(scheduleSOSchema),
    defaultValues: async () => {
      const response = await refetch()
      return buildSOSchedule(serviceOrderState ?? response?.data)
    },
  })

  const { setValue, watch, handleSubmit, register, trigger } = form

  const {
    calendarWeekInterval,
    serviceOrderType,
    schedulePeriod,
    scheduleDate,
    scheduleType,
    technician,
    dayShift,
    customer,
    account,
    status,
    number,
    time,
    id,
  } = watch()

  const { data: serviceOrders, isFetching } = useGetServiceOrders(
    buildGetSOFilters(scheduleDate, calendarWeekInterval, technician?.id),
  )

  const { data: technicianSchedule } = useValidateTechnicianSchedules(
    technician?.id,
    calendarWeekInterval?.startDate,
    calendarWeekInterval?.endDate,
  )

  const { mutate: createIntervention } = useCreateIntervention(id)

  const currentEvents: EventInput[] = useMemo(() => {
    const serviceOrderEvents: EventInput[] = buildServiceOrderEvent(
      serviceOrders?.data,
    )

    const scheduleBlockEvents: EventInput[] =
      buildScheduleBlockEvents(technicianSchedule)

    const filteredScheduleOs = filterSchedules(
      serviceOrderEvents,
      calendarWeekInterval,
    )

    const filteredScheduleBlocks = filterSchedules(
      scheduleBlockEvents,
      calendarWeekInterval,
    )

    const commonExtendedProps = {
      accountCode: account?.code,
      customer: customer?.name,
      type: serviceOrderType,
      number,
      status,
    }

    const scheduleTypes = {
      BUSINESS_HOURS: () => ({
        id,
        allDay: true,
        title: customer?.name,
        start: new Date(scheduleDate),
        extendedProps: {
          schedulePeriod: 'BUSINESS_HOURS',
          ...commonExtendedProps,
        },
      }),
      DAY_SHIFT: () => ({
        id,
        allDay: true,
        title: customer?.name,
        start: new Date(scheduleDate),
        extendedProps: {
          schedulePeriod: dayShift,
          ...commonExtendedProps,
        },
      }),
      FIXED_SCHEDULE: () => {
        const [hours, minutes] = time.split(':')
        const startDate = new Date(scheduleDate)
        startDate.setHours(Number(hours))
        startDate.setMinutes(Number(minutes))

        const endDate = new Date(startDate)
        endDate.setHours(endDate.getHours() + 2)

        return {
          id,
          end: endDate,
          start: startDate,
          title: customer?.name,
          extendedProps: {
            schedulePeriod: undefined,
            ...commonExtendedProps,
          },
        }
      },
    }

    const newEvent = scheduleTypes[scheduleType ?? 'BUSINESS_HOURS']?.()

    const scheduleOsWithoutNewSchedule = filteredScheduleOs?.filter(
      (schedule) => schedule.id !== newEvent?.id,
    )

    return isFetching
      ? []
      : [...scheduleOsWithoutNewSchedule, ...filteredScheduleBlocks, newEvent]
  }, [
    technicianSchedule,
    serviceOrders,
    scheduleDate,
    scheduleType,
    dayShift,
    watch,
    time,
  ])

  const isTechnicianScheduleBlocked = validateTechnicianScheduleBlocked(
    technicianSchedule,
    scheduleDate ? new Date(scheduleDate) : undefined,
    scheduleType as ScheduleType,
    time,
  )

  const hasReachedMaxEvents = useMemo(() => {
    if (!scheduleDate) return false

    const selectedDateEvents = currentEvents.filter(
      (event) =>
        event.start instanceof Date &&
        event.start.toDateString() === new Date(scheduleDate).toDateString(),
    )

    return selectedDateEvents.length - 1 >= MAX_EVENTS_PER_DATE
  }, [currentEvents, scheduleDate])

  const hasServiceOrderAttendantRole = useMemo(
    () =>
      userInfo &&
      userInfo.userCoverages?.primary?.some(
        (coverage) =>
          coverage.coverageType === ATTENDANCE_ROLE.SERVICE_ORDER_ATTENDANT,
      ),
    [userInfo],
  )

  const onSubmit = useCallback(
    (data: ServiceOrderPayload) => {
      const userId = userInfo?.id

      const [hours, minutes] = time?.split(':')

      const shouldInformTime = scheduleType === 'FIXED_SCHEDULE'

      const scheduleDate = createScheduledDate(
        data.scheduleDate,
        shouldInformTime ? hours : undefined,
        shouldInformTime ? minutes : undefined,
      )

      if (serviceOrderState) {
        navigate(buildPath({ path: paths.serviceOrder.create }), {
          state: {
            ...serviceOrderState,
            ...data,
            note: data.note,
            scheduleDate,
            schedulePeriod,
            status:
              isServiceOrderReschedulable(data?.status) && data.id
                ? 'RESCHEDULED'
                : 'SCHEDULED',
          },
        })
        return
      }

      createIntervention(
        {
          userId,
          typeName: 'STATUS_CHANGE',
          note: {
            userId,
            hidden: false,
            note: data.note,
            ...(data.tags?.length && {
              tags: data.tags?.map((tag) => ({ name: tag.name })),
            }),
          },
          attributes: {
            scheduleDate,
            contact: data.contact,
            technicianId: data.technician?.id,
            ...(data.schedulePeriod && {
              schedulePeriod: data.schedulePeriod,
            }),
            status: isServiceOrderReschedulable(data?.status)
              ? 'RESCHEDULED'
              : 'SCHEDULED',
            ...(data.labels?.length && {
              labelIds: data.labels.map((label) => label.id),
            }),
          },
        },
        {
          onSuccess: () => {
            addToast({
              message: `Ordem de serviço ${statusText[data.status]?.success} com sucesso.`,
            })
            navigate(-1)
          },
          onError: () =>
            addToast({
              message: `Não foi possível ${statusText[data.status]?.error} a ordem de serviço. Tente novamente.`,
              type: 'alert',
            }),
        },
      )
    },
    [setValue, userInfo, schedulePeriod, time],
  )

  useEffect(() => {
    register('scheduleDateInput')
    register('serviceOrderType')
    register('schedulePeriod')
    register('dayShift')
    register('account')
    register('labels')
    register('number')
    register('id')
  }, [register])

  return (
    <main className={styles.wrapper}>
      <div className={styles.title}>
        <Text color="element-bold" size="3xl" weight="bold">
          {isServiceOrderReschedulable(status)
            ? 'Reagendamento '
            : 'Agendamento '}
          da OS {number > 0 && `#${number}`}
        </Text>
      </div>
      {
        {
          view: (
            <FormProvider {...form}>
              <form>
                <ConfirmScheduleModal
                  isNewSo={!serviceOrderId}
                  onClose={confirmModal.hide}
                  isVisible={confirmModal.isVisible}
                  onSubmit={handleSubmit(onSubmit)}
                  type={
                    isServiceOrderReschedulable(status)
                      ? 'RESCHEDULED'
                      : 'SCHEDULED'
                  }
                />
                <Form />

                {!!technicianSchedule?.length && (
                  <Popover.Root>
                    <Popover.Trigger asChild>
                      <div className={styles.blockWarning}>
                        <div>
                          <Icon
                            width={18}
                            name="information"
                            color="accent-yellow-default"
                          />
                          <Text color="accent-yellow-bold">
                            Este técnico possui bloqueios na agenda.
                          </Text>
                        </div>
                        <CalendarBlockingDrawer
                          technician={technician}
                          technicianSchedules={technicianSchedule}
                        />
                      </div>
                    </Popover.Trigger>
                  </Popover.Root>
                )}

                <Calendar
                  events={currentEvents}
                  scheduleDate={scheduleDate}
                  {...register('calendarWeekInterval')}
                  onChangeWeekInterval={(newInterval) => {
                    if (isAnotherWeek(calendarWeekInterval, newInterval)) {
                      setValue('calendarWeekInterval', newInterval)
                    }
                  }}
                />
              </form>
            </FormProvider>
          ),
          loading: <Loading />,
          error: <ErrorState />,
        }[
          handleRenderState({
            data: watch(),
            isError: !!serviceOrderId && isError,
            isLoading,
          })
        ]
      }
      <div className={styles.footer}>
        <ButtonV2
          appearance="secondary"
          onClick={() =>
            navigate(
              serviceOrderState
                ? buildPath({ path: paths.serviceOrder.create })
                : -1,
              {
                state: {
                  ...serviceOrderState,
                },
              },
            )
          }
        >
          Voltar
        </ButtonV2>
        <ButtonV2
          appearance="primary"
          onClick={() => {
            trigger()
            confirmModal.show()
          }}
          disabled={
            (isTechnicianScheduleBlocked && !hasServiceOrderAttendantRole) ||
            (hasReachedMaxEvents && !hasServiceOrderAttendantRole)
          }
        >
          Salvar
        </ButtonV2>
      </div>
    </main>
  )
}

export default Schedule
